<template>
  <header class="fixed top-0 left-0 right-0 bg-slate-800 border-b border-slate-700 z-50">
    <div class="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <!-- Logo e navegação -->
        <div class="flex items-center space-x-8">
          <!-- Logo -->
          <div class="flex items-center space-x-2">
            <div class="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <svg class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span class="text-white font-bold text-lg hidden sm:block">HidroTunels</span>
          </div>

          <!-- Navegação principal -->
          <nav class="hidden md:flex space-x-6">
            <RouterLink
              v-for="item in navigation"
              :key="item.name"
              :to="item.to"
              class="nav-link"
              :class="{ 'nav-link-active': $route.name === item.name }"
            >
              <component :is="item.icon" class="h-4 w-4" />
              {{ item.label }}
            </RouterLink>
          </nav>
        </div>

        <!-- Status e ações -->
        <div class="flex items-center space-x-4">
          <!-- Status de conexão -->
          <div class="flex items-center space-x-2 text-sm">
            <div
              :class="[
                'w-2 h-2 rounded-full',
                wsStore.isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
              ]"
            ></div>
            <span class=" hidden sm:block">
              {{ wsStore.isConnected ? 'Conectado' : 'Desconectado' }}
            </span>
          </div>

          <!-- Notificações -->
          <button
            @click="toggleNotifications"
            class="relative btn-icon"
          >
            <BellIcon class="h-5 w-5" />
            <span
              v-if="uiStore.notifications.length > 0"
              class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
            >
              {{ uiStore.notifications.length }}
            </span>
          </button>

          <!-- Menu do usuário -->
          <div class="relative" ref="userMenuRef">
            <button
              @click="toggleUserMenu"
              class="flex items-center space-x-2 btn-icon"
            >
              <div class="h-8 w-8 bg-gray-600 rounded-full flex items-center justify-center">
                <UserIcon class="h-5 w-5" />
              </div>
              <span class="text-white text-sm hidden sm:block">
                {{ authStore.userDisplayName }}
              </span>
              <ChevronDownIcon class="h-4 w-4 text-gray-400" />
            </button>

            <!-- Dropdown do usuário -->
            <Transition name="dropdown">
              <div
                v-if="showUserMenu"
                class="absolute right-0 mt-2 w-56 bg-slate-800 rounded-lg shadow-xl border border-slate-600 py-1 z-50"
              >
                <!-- quem esta logado -->
                <div class="px-3 py-2 border-b border-slate-700 mb-1">
                  <p class="text-sm text-white truncate">{{ authStore.user?.full_name || authStore.user?.username }}</p>
                  <p class="text-xs text-gray-400 truncate">{{ authStore.user?.email }}</p>
                </div>

                <RouterLink
                  to="/profile"
                  class="dropdown-item flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-slate-700 transition-colors"
                  @click="showUserMenu = false"
                >
                  <UserIcon class="h-4 w-4 text-gray-400 shrink-0" />
                  Perfil
                </RouterLink>
                
                <RouterLink
                  to="/settings"
                  class="dropdown-item flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-slate-700 transition-colors"
                  @click="showUserMenu = false"
                >
                  <CogIcon class="h-4 w-4 text-gray-400 shrink-0" />
                  Configurações
                </RouterLink>
                
                <RouterLink
                  v-if="authStore.isAdmin"
                  to="/usuarios"
                  class="dropdown-item flex items-center gap-2.5 px-3 py-2 text-sm text-gray-200 hover:bg-slate-700 transition-colors"
                  @click="showUserMenu = false"
                >
                  <UsersIcon class="h-4 w-4 text-gray-400 shrink-0" />
                  Usuários
                </RouterLink>
                
                <hr class="border-slate-700 my-1">
                
                <button
                  @click="handleLogout"
                  class="dropdown-item flex items-center gap-2.5 w-full px-3 py-2 text-sm text-left text-red-400 hover:bg-slate-700 hover:text-red-300 transition-colors"
                >
                  <ArrowRightOnRectangleIcon class="h-4 w-4" />
                  Sair
                </button>
              </div>
            </Transition>
          </div>

          <!-- Menu mobile -->
          <button
            @click="uiStore.toggleSidebar"
            class="md:hidden btn-icon"
          >
            <Bars3Icon class="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>

    <!-- Sidebar mobile -->
    <Transition name="sidebar">
      <div
        v-if="uiStore.sidebarOpen"
        class="md:hidden fixed inset-0 z-40 bg-black bg-opacity-50"
        @click="uiStore.toggleSidebar"
      >
        <div
          class="fixed right-0 top-0 h-full w-64 bg-slate-800 shadow-lg transform"
          @click.stop
        >
          <div class="p-4">
            <div class="flex items-center justify-between mb-6">
              <span class="text-white font-bold">Menu</span>
              <button @click="uiStore.toggleSidebar" class="btn-icon">
                <XMarkIcon class="h-5 w-5" />
              </button>
            </div>
            
            <nav class="space-y-2">
              <RouterLink
                v-for="item in navigation"
                :key="item.name"
                :to="item.to"
                class="mobile-nav-link"
                @click="uiStore.toggleSidebar"
              >
                <component :is="item.icon" class="h-5 w-5" />
                {{ item.label }}
              </RouterLink>
            </nav>
          </div>
        </div>
      </div>
    </Transition>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { RouterLink } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores'
import { useTunnelStore, useWebSocketStore } from '@/stores'
import {
  BellIcon, UserIcon, ChevronDownIcon, CogIcon, UsersIcon,
  ArrowRightOnRectangleIcon, Bars3Icon, XMarkIcon,
  HomeIcon, WifiIcon, DocumentTextIcon, CircleStackIcon
} from '@heroicons/vue/24/outline'

const authStore = useAuthStore()
const uiStore = useUIStore()
const tunnelStore = useTunnelStore()
const wsStore = useWebSocketStore()

const showUserMenu = ref(false)
const showNotifications = ref(false)
const userMenuRef = ref(null)

// Navegação
const navigation = computed(() => {
  const items = [
    { name: 'Dashboard', label: 'Dashboard', to: '/', icon: HomeIcon },
    { name: 'Tunnels', label: 'Túneis', to: '/tunnels', icon: WifiIcon },
    { name: 'Logs', label: 'Logs', to: '/logs', icon: DocumentTextIcon },
    { name: 'Data', label: 'Dados', to: '/data', icon: CircleStackIcon },
  ]
  
  return items
})

function toggleUserMenu() {
  showUserMenu.value = !showUserMenu.value
  showNotifications.value = false
}

function toggleNotifications() {
  showNotifications.value = !showNotifications.value
  showUserMenu.value = false
}

async function handleLogout() {
  showUserMenu.value = false
  await authStore.logout()
}

// Fechar menus ao clicar fora
function handleClickOutside(event) {
  if (userMenuRef.value && !userMenuRef.value.contains(event.target)) {
    showUserMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  // Conectar WebSocket dos túneis
  tunnelStore.initializeWebSocket()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>

</style>